// deps
import React, { memo, ReactElement, useCallback, useMemo } from "react";

// internal deps
import { User } from "@swiggy-private/payment-core";

// helpers
import {
    canEnablePaymentMethod,
    getPaymentMethodDiscount,
    getPaymentMethodDisplayName,
    getPaymentMethodIconUrl,
} from "@payment-ui/helpers/Payment";

// interfaces
import { ApiFetchState as FetchState } from "@swiggy-private/payment-core";
import { IPayLaterPaymentGroup, PayLaterPaymentMethodData } from "@payment-ui/interfaces/PayLater";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// components
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import OtherPaymentOffersGenericUI from "../../OtherPaymentOffersGenericUI";

const PAY_LATER_PAYMENT_ICON_WIDTH = 32;

interface PayLaterProps extends PaymentMethodProps {
    key: string;
    methodData: PayLaterPaymentMethodData;
    paymentActions: IPayLaterPaymentGroup["paymentActions"];
    user: User;
}

function PayLaterPaymentMethod(props: PayLaterProps): ReactElement {
    const enabled = useMemo(() => canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled), [
        props.isPaymentGroupEnabled,
        props.methodData,
    ]);
    const { checkPayLaterLinkStatus, onSelect } = props.paymentActions;
    const [fetchState] = checkPayLaterLinkStatus(props.methodData);

    // handle click on lazypay method
    const onClick = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();
            if (!enabled || fetchState !== FetchState.SUCCESS) {
                return;
            }

            onSelect(props.methodData);
        },
        [fetchState, props.methodData, onSelect, enabled],
    );

    const paymentMethodIcon = useMemo(
        (): ReactElement => (
            <PaymentMethodIcon
                src={getPaymentMethodIconUrl(props.methodData)}
                alt={props.methodData.displayName}
                width={PAY_LATER_PAYMENT_ICON_WIDTH}
            />
        ),
        [props.methodData],
    );

    const displayName = getPaymentMethodDisplayName(props.methodData);
    const subtext = getPaymentMethodDiscount(props.methodData);

    return (
        <OtherPaymentOffersGenericUI
            onClick={onClick}
            displayName={displayName}
            subtext={subtext}
            paymentMethodIcon={paymentMethodIcon}
        />
    );
}

export default memo(PayLaterPaymentMethod);
